import React, { useEffect, useState } from 'react';
import { Button, Divider, Header, Segment, ButtonProps } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { useRouter } from 'next/router';

import * as actions from '../../actions';
import { CustomInfiniteScroll } from '../common';
import { SEARCH_ALL_LIMIT } from '../../constants';
import { selectSearchResults } from '../../selectors';
import { RootState } from '../../types/redux';

import Articles from '../ptt/Search/Articles';
import Boards from '../ptt/Search/Boards';
import Users from '../ptt/Search/Users';

import Forums from '../dcard/Search/Forums';
import Personas from '../dcard/Search/Personas';
import Posts from '../dcard/Search/Posts';
import Schools from '../dcard/Search/Schools';

const PTT_ACTIVE_INDICES = ['article', 'user', 'board'] as const;
const DCARD_ACTIVE_INDICES = ['persona', 'forum', 'post', 'school'] as const;

type ActiveIndex = 'article' | 'user' | 'board' | 'persona' | 'forum' | 'post' | 'school';
type Platform = 'ptt' | 'dcard';

interface Props {
  articles: any[];
  boards: any[];
  forums: any[];
  personas: any[];
  posts: any[];
  schools: any[];
  users: any[];
  authed?: boolean;
  isMobile: boolean;
  isSearching: boolean;
  searchOffset: number;
  searchTerm: string;
  platform: Platform;
  SearchAll: (platform: string, searchTerm: string, limit: number, offset: number) => void;
}

const getDefaultActiveIndex = (platform: Platform): ActiveIndex => {
  switch (platform) {
    case 'ptt':
      return 'article';
    case 'dcard':
      return 'post';
    default:
      throw new Error(`Unsupported platform ${platform}`);
  }
};

const Search: React.FC<Props> = (props) => {
  const router = useRouter();
  const { platform: queryPlatform, q: querySearchTerm } = router.query;

  const [activeIndex, setActiveIndex] = useState<ActiveIndex>(
    getDefaultActiveIndex(queryPlatform as Platform),
  );

  useEffect(() => {
    props.SearchAll(queryPlatform as string, querySearchTerm as string, SEARCH_ALL_LIMIT, 0);
  }, [queryPlatform, querySearchTerm]);

  useEffect(() => {
    if (
      props.platform === 'dcard' &&
      (PTT_ACTIVE_INDICES as readonly string[]).includes(activeIndex)
    ) {
      setActiveIndex('post');
    }
    if (
      props.platform === 'ptt' &&
      (DCARD_ACTIVE_INDICES as readonly string[]).includes(activeIndex)
    ) {
      setActiveIndex('article');
    }
  }, [props.platform]);

  const hasMore = (): boolean => {
    return getLength(activeIndex) % SEARCH_ALL_LIMIT === 0;
  };

  const fetchMore = () => {
    const {
      SearchAll,
      isSearching,
      platform,
      searchOffset,
      searchTerm,
    } = props;

    if (!isSearching) {
      SearchAll(
        platform,
        searchTerm,
        SEARCH_ALL_LIMIT,
        searchOffset + SEARCH_ALL_LIMIT,
      );
    }
  };

  const handleSwitchCatalog = (e: React.MouseEvent<HTMLButtonElement>, { name }: ButtonProps) => {
    setActiveIndex(name as ActiveIndex);
  };

  const getLength = (activeIndex: ActiveIndex): number => {
    const { articles, boards, forums, personas, posts, schools, users } = props;

    switch (activeIndex) {
      case 'article':
        return articles.length;
      case 'user':
        return users.length;
      case 'board':
        return boards.length;
      case 'persona':
        return personas.length;
      case 'school':
        return schools.length;
      case 'forum':
        return forums.length;
      case 'post':
        return posts.length;
      default:
        throw new Error('Unsupported index name');
    }
  };

  const getTotalLength = (): number => {
    switch (props.platform) {
      case 'ptt':
        return getLength('article') + getLength('board') + getLength('user');
      case 'dcard':
        return (
          getLength('persona') +
          getLength('forum') +
          getLength('post') +
          getLength('school')
        );
      default:
        return 0;
    }
  };

  const getDescriptor = (activeIndex: ActiveIndex): string => {
    switch (activeIndex) {
      case 'article':
        return 'PTT 文章';
      case 'user':
        return 'PTT 用戶ID';
      case 'board':
        return 'PTT 看板';
      case 'post':
        return 'Dcard 文章';
      case 'persona':
        return 'Dcard 用戶ID';
      case 'forum':
        return 'Dcard 看板';
      case 'school':
        return 'Dcard 學校';
      default:
        throw new Error('Invalid activeIndex');
    }
  };

  const {
    authed,
    articles,
    boards,
    forums,
    isMobile,
    isSearching,
    personas,
    platform,
    posts,
    schools,
    searchTerm,
    users,
  } = props;

  const totalLength = getTotalLength();

  if (!isSearching && totalLength === 0) {
    return (
      <Header
        as={isMobile ? 'h3' : 'h1'}
        content="Oops...沒有找到任何結果喔"
        color="grey"
      />
    );
  }

  if (isSearching && totalLength === 0) {
    return (
      <Header
        as={isMobile ? 'h3' : 'h1'}
        content="搜尋中......."
        color="grey"
      />
    );
  }

  return (
    <div>
      <Button.Group size={isMobile ? 'tiny' : 'large'}>
        {platform === 'ptt' && (
          <div>
            <Button
              icon="edit"
              name="article"
              content="文章"
              active={activeIndex === 'article'}
              onClick={handleSwitchCatalog}
            />
            <Button
              icon="spy"
              name="user"
              content="使用者ID"
              active={activeIndex === 'user'}
              onClick={handleSwitchCatalog}
            />
            <Button
              icon="list"
              name="board"
              content="板"
              active={activeIndex === 'board'}
              onClick={handleSwitchCatalog}
            />
          </div>
        )}
        {platform === 'dcard' && (
          <div>
            <Button
              icon="edit"
              name="post"
              content="文章"
              active={activeIndex === 'post'}
              onClick={handleSwitchCatalog}
            />
            <Button
              icon="spy"
              name="persona"
              content="使用者ID"
              active={activeIndex === 'persona'}
              onClick={handleSwitchCatalog}
            />
            <Button
              icon="list"
              name="forum"
              content="板"
              active={activeIndex === 'forum'}
              onClick={handleSwitchCatalog}
            />
            <Button
              icon="book"
              name="school"
              content="學校"
              active={activeIndex === 'school'}
              onClick={handleSwitchCatalog}
            />
          </div>
        )}
      </Button.Group>
      <Divider horizontal>
        總共 {getLength(activeIndex)} 筆 {getDescriptor(activeIndex)}
      </Divider>
      {activeIndex === 'article' &&
        (getLength(activeIndex) > 0 ? (
          <CustomInfiniteScroll
            dataLength={getLength(activeIndex)}
            fetchMore={fetchMore}
            hasMore={hasMore()}
          >
            <Segment>
              <Articles data={articles} searchTerm={searchTerm} />
            </Segment>
          </CustomInfiniteScroll>
        ) : (
          <Header as="h3" content="沒有找到符合的文章喔" color="grey" />
        ))}
      {activeIndex === 'user' &&
        (getLength(activeIndex) > 0 ? (
          <CustomInfiniteScroll
            dataLength={getLength(activeIndex)}
            fetchMore={fetchMore}
            hasMore={hasMore()}
          >
            <Segment>
              <Users data={users} />
            </Segment>
          </CustomInfiniteScroll>
        ) : (
          <Header as="h3" content="沒有找到符合的使用者喔" color="grey" />
        ))}
      {activeIndex === 'board' &&
        (getLength(activeIndex) > 0 ? (
          <CustomInfiniteScroll
            dataLength={getLength(activeIndex)}
            fetchMore={fetchMore}
            hasMore={hasMore()}
          >
            <Segment>
              <Boards data={boards} />
            </Segment>
          </CustomInfiniteScroll>
        ) : (
          <Header as="h3" content="沒有找到符合的板名喔" color="grey" />
        ))}
      {activeIndex === 'persona' &&
        (getLength(activeIndex) > 0 ? (
          <CustomInfiniteScroll
            dataLength={getLength(activeIndex)}
            fetchMore={fetchMore}
            hasMore={hasMore()}
          >
            <Segment>
              <Personas data={personas} />
            </Segment>
          </CustomInfiniteScroll>
        ) : (
          <Header as="h3" content="沒有找到符合的使用者喔" color="grey" />
        ))}
      {activeIndex === 'forum' &&
        (getLength(activeIndex) > 0 ? (
          <CustomInfiniteScroll
            dataLength={getLength(activeIndex)}
            fetchMore={fetchMore}
            hasMore={hasMore()}
          >
            <Segment>
              <Forums data={forums} />
            </Segment>
          </CustomInfiniteScroll>
        ) : (
          <Header as="h3" content="沒有找到符合的板名喔" color="grey" />
        ))}
      {activeIndex === 'post' &&
        (getLength(activeIndex) > 0 ? (
          <CustomInfiniteScroll
            dataLength={getLength(activeIndex)}
            fetchMore={fetchMore}
            hasMore={hasMore()}
          >
            <Segment>
              <Posts authed={authed || false} data={posts} searchTerm={searchTerm} />
            </Segment>
          </CustomInfiniteScroll>
        ) : (
          <Header as="h3" content="沒有找到符合的文章喔" color="grey" />
        ))}
      {activeIndex === 'school' &&
        (getLength(activeIndex) > 0 ? (
          <CustomInfiniteScroll
            dataLength={getLength(activeIndex)}
            fetchMore={fetchMore}
            hasMore={hasMore()}
          >
            <Segment>
              <Schools data={schools} />
            </Segment>
          </CustomInfiniteScroll>
        ) : (
          <Header as="h3" content="沒有找到符合的學校喔" color="grey" />
        ))}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  ...selectSearchResults(state),
  isMobile: state.browser.lessThan.medium,
  isSearching: state.search.isFetching || false,
  searchOffset: (state.search as any).offset || 0,
  searchTerm: (state.search as any).q || '',
  platform: (state.search as any).platform || 'ptt',
});

export default connect(mapStateToProps, actions)(Search);